// ============================================
// Memory Edit Module - Accordion (ES6)
// 아코디언 컴포넌트
// ============================================

import { escapeHtml } from '../utils.js';
import {
  isAccordionOpen,
  toggleAccordion,
  getLayerData,
  isLayerLoading
} from './state.js';

// ============================================
// 레이어 설정
// ============================================

export const LAYER_CONFIG = {
  event: {
    title: '사건',
    color: '#FBBF24'
  },
  quote: {
    title: '대사',
    color: '#A78BFA'
  },
  summary: {
    title: '요약',
    color: '#10B981'
  }
};

export const LAYER_ORDER = ['event', 'quote', 'summary'];

// ============================================
// 아코디언 렌더링
// ============================================

export function renderAccordion(renderLayerContent) {
  const html = LAYER_ORDER.map(layer => {
    const config = LAYER_CONFIG[layer];
    const isOpen = isAccordionOpen(layer);
    const data = getLayerData(layer);
    const isLoading = isLayerLoading(layer);
    const count = getLayerCount(layer, data);

    return `
      <div class="memory-accordion-item ${isOpen ? 'open' : ''}" data-layer="${layer}">
        <div class="memory-accordion-header" data-action="toggleAccordion" data-layer="${layer}">
          <span class="accordion-title">${config.title}</span>
          ${count > 0 ? `<span class="accordion-count">${count}</span>` : ''}
          <svg class="accordion-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
        <div class="memory-accordion-content">
          ${isLoading ? renderLoadingState() : renderLayerContent(layer, data)}
        </div>
      </div>
    `;
  }).join('');

  return `<div class="memory-accordion">${html}</div>`;
}

// ============================================
// 아이템 카운트
// ============================================

function getLayerCount(layer, data) {
  if (!data) return 0;

  switch (layer) {
    case 'event':
    case 'quote':
      return Array.isArray(data) ? data.length : 0;
    default:
      return 0;
  }
}

// ============================================
// 로딩 상태
// ============================================

function renderLoadingState() {
  return `
    <div class="memory-layer-loading">
      <div class="spinner"></div>
    </div>
  `;
}

// ============================================
// 빈 상태
// ============================================

export function renderEmptyState(layer) {
  const config = LAYER_CONFIG[layer];
  return `
    <div class="memory-layer-empty">
      <span class="empty-text">아직 ${config.title} 정보가 없습니다</span>
      ${canCreateItem(layer) ? `
        <button class="memory-add-btn" data-action="createMemory" data-layer="${layer}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          추가
        </button>
      ` : ''}
    </div>
  `;
}

// ============================================
// 레이어 헤더 (추가 버튼 포함)
// ============================================

export function renderLayerHeader(layer) {
  if (!canCreateItem(layer)) return '';

  return `
    <div class="memory-layer-header">
      <button class="memory-add-inline-btn" data-action="createMemory" data-layer="${layer}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        추가
      </button>
    </div>
  `;
}

// ============================================
// 추가 가능 여부
// ============================================

function canCreateItem(layer) {
  // event, quote는 사용자가 추가 가능
  return ['event', 'quote'].includes(layer);
}

// ============================================
// 아코디언 토글 애니메이션
// ============================================

export function animateAccordionToggle(layerElement, isOpening) {
  const content = layerElement.querySelector('.memory-accordion-content');
  if (!content) return;

  if (isOpening) {
    // 먼저 실제 높이 측정
    content.style.maxHeight = 'none';
    const height = content.scrollHeight;
    content.style.maxHeight = '0px';

    // 다음 프레임에서 애니메이션 시작
    requestAnimationFrame(() => {
      content.style.maxHeight = height + 'px';
      layerElement.classList.add('open');

      // 애니메이션 완료 후 max-height: none으로 변경 (동적 확장 허용)
      setTimeout(() => {
        if (layerElement.classList.contains('open')) {
          content.style.maxHeight = 'none';
        }
      }, 300);
    });
  } else {
    // 닫기 전 현재 높이 고정
    content.style.maxHeight = content.scrollHeight + 'px';
    requestAnimationFrame(() => {
      content.style.maxHeight = '0px';
      layerElement.classList.remove('open');
    });
  }
}

// ============================================
// 아코디언 콘텐츠 높이 업데이트 (히스토리 토글 등)
// ============================================

export function updateAccordionHeight(layer) {
  const layerElement = document.querySelector(`.memory-accordion-item[data-layer="${layer}"]`);
  if (!layerElement || !layerElement.classList.contains('open')) return;

  const content = layerElement.querySelector('.memory-accordion-content');
  if (content) {
    content.style.maxHeight = 'none';
  }
}

// ============================================
// 아코디언 이벤트 핸들러
// ============================================

export function handleAccordionToggle(layer, onLayerOpen) {
  const wasOpen = isAccordionOpen(layer);
  toggleAccordion(layer);

  const layerElement = document.querySelector(`.memory-accordion-item[data-layer="${layer}"]`);
  if (layerElement) {
    animateAccordionToggle(layerElement, !wasOpen);
  }

  // 열릴 때 데이터 로드 콜백
  if (!wasOpen && onLayerOpen) {
    onLayerOpen(layer);
  }
}
